import { ConnectIcon } from '@/components/icons'
import { helpTopicUrl } from '@/lib/help-links'
import { AuthMode, type BlockConfig } from '@/blocks/types'
import {
  getModelOptions,
  getProviderCredentialSubBlocks,
  getSerializedModelProviderId,
  PROVIDER_CREDENTIAL_INPUTS,
} from '@/blocks/utils'
import type { ToolResponse } from '@/tools/types'

/**
 * Port-based routing: the block declares its routes and their descriptions, and
 * the model picks one. An earlier definition routed by naming a downstream
 * block instead and the two lived side by side; nothing has shipped, so there
 * is one router again.
 */
interface RouterResponse extends ToolResponse {
  output: {
    context: string
    model: string
    tokens?: {
      prompt?: number
      completion?: number
      total?: number
    }
    cost?: {
      input: number
      output: number
      total: number
    }
    selectedRoute: string
    reasoning: string
    selectedPath: {
      blockId: string
      blockType: string
      blockTitle: string
    }
  }
}

export const RouterBlock: BlockConfig<RouterResponse> = {
  type: 'router',
  name: 'Router',
  description: 'Route workflow based on context',
  authMode: AuthMode.ApiKey,
  docsLink: helpTopicUrl('workflows'),
  longDescription:
    'Intelligently route workflow execution to different paths based on context analysis. Define multiple routes with descriptions, and an LLM will determine which route to take based on the provided context.',
  bestPractices: `
  - Write clear, specific descriptions for each route
  - The context field should contain all relevant information for routing decisions
  - Route descriptions should be mutually exclusive when possible
  - Use descriptive route names to make the workflow readable
  `,
  category: 'blocks',
  bgColor: '#28C43F',
  icon: ConnectIcon,
  subBlocks: [
    {
      id: 'context',
      title: 'Context',
      type: 'long-input',
      placeholder: 'Enter the context to analyze for routing...',
      required: true,
    },
    {
      id: 'routes',
      type: 'router-input',
    },
    {
      id: 'model',
      title: 'Model',
      type: 'combobox',
      placeholder: 'Type or select a model...',
      required: true,
      defaultValue: 'claude-sonnet-5',
      options: getModelOptions,
    },
    ...getProviderCredentialSubBlocks(),
  ],
  tools: {
    access: [
      'openai_chat',
      'anthropic_chat',
      'google_chat',
      'xai_chat',
      'deepseek_chat',
      'deepseek_reasoner',
    ],
    config: {
      tool: (params: Record<string, any>) => getSerializedModelProviderId(params.model),
    },
  },
  inputs: {
    context: { type: 'string', description: 'Context for routing decision' },
    routes: { type: 'json', description: 'Route definitions with descriptions' },
    model: { type: 'string', description: 'AI model to use' },
    ...PROVIDER_CREDENTIAL_INPUTS,
  },
  outputs: {
    context: { type: 'string', description: 'Context used for routing' },
    model: { type: 'string', description: 'Model used' },
    tokens: { type: 'json', description: 'Token usage' },
    cost: { type: 'json', description: 'Cost information' },
    selectedRoute: { type: 'string', description: 'Selected route ID' },
    reasoning: { type: 'string', description: 'Explanation of why this route was chosen' },
    selectedPath: { type: 'json', description: 'Selected routing path' },
  },
}
